import {createSelectorCreator} from '@reduxjs/toolkit';
import isEqual from 'react-fast-compare';
import {shallowEqual} from 'react-redux';
import {lruMemoize} from 'reselect';

import {
  countSelectorRecomputation,
  countSelectorResultEqualityCheck,
  InstrumentedDeepCompareSite,
  isTableCountingActive,
  makeCountedIsEqual,
} from 'notebook/tables/perf/TableInstrumentation';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * It can be difficult to debug exactly when selectors are firing and when the memoization is not working as
 * expected.  So this flag can be set to true to enable debug logging for all selector comparisons.
 *
 * This flag should never be checked in as true.
 */
const DEBUG_ACTIVE = false;

/**
 * This is a helper function to log differences between two objects when the debug flag is active.
 * The two inputs can truly be any, and if it is an object then it will compare each key to see which one
 * is different.
 */

function logDiffs(
  a: unknown,
  b: unknown,
  name: string,
  comparator: (a: unknown, b: unknown) => boolean
): void {
  if (DEBUG_ACTIVE) {
    // oxlint-disable-next-line no-console
    console.log(name, 'not equal', a, b);
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') {
      return;
    }
    const aEntries = Object.entries(a);
    const bEntries = Object.entries(b);
    for (const [key, aValue] of aEntries) {
      const bValue = bEntries.find(([bKey]) => bKey === key);
      if (!comparator(aValue, bValue)) {
        // oxlint-disable-next-line no-console
        console.log(name, 'key not equal', key, aValue, bValue);
      }
    }
  }
}

/**
 * This creates a selector that will provide deep equality checks for the result of each given selector
 *
 * This is intended for objects where you care about the internal details and want to fully scan the objects
 * for changes.
 */
export const createDeepSelector = createSelectorCreator({
  memoize: lruMemoize,
  argsMemoize: lruMemoize,
  memoizeOptions: {
    resultEqualityCheck: (a, b) => {
      // console.log('Deep Selector Check', a, b);
      const result = isEqual(a, b);
      if (!result) {
        logDiffs(a, b, 'Deep Selector', isEqual);
      }
      return result;
    },
  },
  devModeChecks: {
    // Allow identity functions to be memoized since this memoization provides protection against
    // the state being volatile
    identityFunctionCheck: 'never',
  },
});

/**
 * This creates a deep-comparing selector whose result comparisons are counted against `site`, for
 * the deep compares hot enough that the table's performance suite asserts on how much of the cell
 * matrix they walk.
 *
 * Counting is only wired in for a selector built while a scenario is counting; otherwise this is
 * {@link createDeepSelector}.
 */
export function createCountedDeepSelector(site: InstrumentedDeepCompareSite): typeof createDeepSelector {
  if (!isTableCountingActive()) {
    return createDeepSelector;
  }
  const countedIsEqual = makeCountedIsEqual(site);
  return createSelectorCreator({
    memoize: lruMemoize,
    argsMemoize: lruMemoize,
    memoizeOptions: {
      resultEqualityCheck: (a, b) => {
        const result = countedIsEqual(a, b);
        if (!result) {
          logDiffs(a, b, 'Deep Selector', isEqual);
        }
        return result;
      },
    },
    devModeChecks: {
      identityFunctionCheck: 'never',
    },
  });
}

/**
 * This creates a selector that will provide shallow equality checks for the result of each given selector
 *
 * This is intended for objects where you don't care about the internal details, but want to be resilient to
 * just the external object reference changing.  For example if the array reference changes but the contents
 * do not.
 */
export const createShallowSelector = createSelectorCreator({
  memoize: lruMemoize,
  argsMemoize: lruMemoize,
  argsMemoizeOptions: {
    resultEqualityCheck: (a, b) => {
      // console.log('Shallow Selector Check', a, b);
      const result = shallowEqual(a, b);
      if (!result) {
        logDiffs(a, b, 'Shallow Selector', shallowEqual);
      }
      return result;
    },
  },
});

/**
 * This creates a selector that behaves identical to the default createSelector, but will log differences
 * when debug is active.
 */
export const createDebuggableSelector = createSelectorCreator({
  memoize: lruMemoize,
  argsMemoize: lruMemoize,
  memoizeOptions: {
    resultEqualityCheck: (a, b) => {
      // console.log('Debuggable Selector Check', a, b);
      const result = a === b;
      if (!result) {
        logDiffs(a, b, 'Debuggable Selector', (a, b) => a === b);
      }
      return result;
    },
  },
});

type StateSelector<S, R> = (state: S) => R;

interface SelectorInputMap<S> {
  readonly [K: string]: StateSelector<S, unknown>;
}

/** Maps an object of input selectors to the object the combiner receives. */
type SelectorResults<S, M extends SelectorInputMap<S>> = {
  [K in keyof M]: M[K] extends StateSelector<S, infer R> ? R : never;
};

/**
 * Creates a memoized selector over the table store from an object of named input selectors.
 *
 * Reselect takes its inputs as a positional array whose order has to line up with the combiner's
 * parameters, which is only enforced by convention. Naming the inputs instead makes the combiner
 * read them by key, so adding or reordering an input cannot silently shift the others.
 *
 * `name` identifies the selector in the table's performance suite, which asserts on how often each
 * selector recomputes. It is only used for instrumentation, so it has to be unique across selectors
 * rather than across the selector instances a table creates, and only a selector built while a
 * scenario is counting is instrumented at all.
 */
export function createTableStoreSelector<M extends SelectorInputMap<TableEditableStore>, R>(
  name: string,
  inputs: M,
  combiner: (results: SelectorResults<TableEditableStore, M>) => R
): (store: TableEditableStore) => R {
  const entries = Object.entries(inputs);
  const combineNamedInputs = (...values: Array<unknown>): R =>
    combiner(
      Object.fromEntries(entries.map(([key], index) => [key, values[index]])) as SelectorResults<
        TableEditableStore,
        M
      >
    );
  const inputSelectors = entries.map(([, selector]) => selector);

  if (!isTableCountingActive()) {
    return createDebuggableSelector.withTypes<TableEditableStore>()(inputSelectors, combineNamedInputs);
  }

  return createSelectorCreator({
    memoize: lruMemoize,
    argsMemoize: lruMemoize,
    memoizeOptions: {
      resultEqualityCheck: (a, b) => {
        countSelectorResultEqualityCheck(name);
        const result = a === b;
        if (!result) {
          logDiffs(a, b, 'Debuggable Selector', (a, b) => a === b);
        }
        return result;
      },
    },
  }).withTypes<TableEditableStore>()(inputSelectors, (...values: Array<unknown>) => {
    countSelectorRecomputation(name);
    return combineNamedInputs(...values);
  });
}
